import { ReactNode, use, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Divider from 'shared/components/divider/divider';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';

import VirtualizedTreeItem from 'features/organisation/views/organisation-details/components/electronic-client-record/components/link-room-modal/components/link-room-table/components/virtualized-tree-item/virtualized-tree-item';
import useLinkRoomTableHook from 'features/organisation/views/organisation-details/components/electronic-client-record/components/link-room-modal/components/link-room-table/hooks/link-room-table-hook';
import useVirtualizedTree, {
  FlattenedLocation,
} from 'features/organisation/views/organisation-details/components/electronic-client-record/components/link-room-modal/components/link-room-table/hooks/use-virtualized-tree';
import { LocationType } from 'features/organisation/views/organisation-details/components/electronic-client-record/components/link-room-modal/domain/models/location-type';
import ElectronicClientRecordProviderContext from 'features/organisation/views/organisation-details/components/electronic-client-record/providers/contexts/electronic-client-record-context';
import styles from './link-room-table.module.scss';

const LinkRoomTable = (): React.JSX.Element => {
  const { locationsData, locationsError, locationsIsLoading, selectedTableRoom } = use(ElectronicClientRecordProviderContext);
  const { onClick } = useLinkRoomTableHook();
  const { flattenedLocations, toggleExpanded, isExpanded } = useVirtualizedTree(locationsData);

  const { t } = useTranslation('organisation', { keyPrefix: 'electronicClientRecord.linkRoomModal' });

  const labelRender = useCallback(
    (labelText: string, isSelectable: boolean, location: FlattenedLocation): ReactNode => {
      const isSelected = selectedTableRoom?.id === location.id;

      return (
        <div className={styles.locationLabelContainer}>
          {isSelectable && (
            <Checkbox
              checked={isSelected}
              onChange={(): void => {
                onClick(location.id);
              }}
              size="small"
              sx={{ marginRight: 1 }}
            />
          )}
          <Typography sx={{ paddingLeft: isSelectable ? '0px' : '8px' }} className="ellipsis" variant="subtitle1">
            {labelText}
          </Typography>
        </div>
      );
    },
    [selectedTableRoom, onchange],
  );

  const handleItemClick = useCallback(
    (location: (typeof flattenedLocations)[0]) => {
      const isSelectable = location.type === LocationType.Room;
      if (isSelectable) {
        onClick(location.id);
      }
    },
    [onClick],
  );

  const handleToggleExpansion = useCallback(
    (locationId: string) => {
      toggleExpanded(locationId);
    },
    [toggleExpanded],
  );

  const renderContent = (): ReactNode => {
    if (locationsError || !locationsData || locationsData?.length === 0) {
      return <>{t('noRooms')}</>;
    }

    return (
      <div className={styles.virtualizationContainer}>
        {flattenedLocations.map((location, index) => {
          const isSelectable = location.type === LocationType.Room;
          const isFirst = index === 0;

          return (
            <div key={location.id}>
              {isFirst && <Divider className={styles.divider} />}
              <VirtualizedTreeItem
                itemId={location.id}
                label={labelRender(location.name, isSelectable, location)}
                onClick={() => handleItemClick(location)}
                isSelectable={isSelectable}
                level={location.level}
                hasChildren={location.hasChildren}
                isExpanded={isExpanded(location.id)}
                onToggle={() => handleToggleExpansion(location.id)}
                containerSelector={`.${styles.virtualizationContainer}`}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return <>{locationsIsLoading ? <LoadingIndicator /> : renderContent()}</>;
};

export default LinkRoomTable;
